import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { Beneficiary } from "../../../types/beneficiaries";
import { hasValidNames } from "./utils/beneficiaryUtils";
import ProgressIndicator from "../ProgressIndicator";
import { WizardStepError } from "../WizardStepError";
import { WizardStepSkeleton } from "../WizardStepSkeleton";
import BeneficiaryActions from "./components/BeneficiaryActions";
import BeneficiaryList from "./components/BeneficiaryList";
import { useBeneficiaryManagement } from "./hooks/useBeneficiaryManagement";
import { useBeneficiaryValidation } from "./hooks/useBeneficiaryValidation";

const Beneficiaries: React.FC<{
  handleNext: () => void;
  handlePrevious: () => void;
  isUserEnrolled?: boolean;
  onCancel?: () => void;
}> = ({ handleNext, handlePrevious, isUserEnrolled, onCancel }) => {
  const { t } = useTranslation();
  const { onboardingProgress } = useSelector((state: RootState) => state.user);

  const {
    beneficiaries,
    setBeneficiaries,
    loading,
    isSubmitting,
    error,
    // isSuccess,
    handleFetchBeneficiariesFromAPI,
    handleAddBeneficiary,
    handleDeleteBeneficiary,
    processAddOrUpdateBeneficiaries,
  } = useBeneficiaryManagement();

  const { validateAndProcessBeneficiaries } = useBeneficiaryValidation();

  // Simple check for special characters in names
  const hasNoSpecialChars = hasValidNames(beneficiaries);

  useEffect(() => {
    handleFetchBeneficiariesFromAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBeneficiaryChange = (
    index: number,
    field: keyof Beneficiary,
    value: string,
  ) => {
    setBeneficiaries((prev) =>
      prev.map((b, i) => (i === index ? { ...b, [field]: value } : b)),
    );
  };

  const handleAddOrUpdateBeneficiary = async () => {
    await validateAndProcessBeneficiaries(
      beneficiaries,
      error,
      async (validBeneficiaries) => {
        const success =
          await processAddOrUpdateBeneficiaries(validBeneficiaries);
        if (success) {
          handleNext();
        }
        return success;
      },
    );
  };

  return (
    <div className="w-full mx-auto font-outfit sm:w-[640px]">
      <ProgressIndicator />

      {!isUserEnrolled && (
        <div className="flex flex-col gap-[10px] text-[1rem]">
          <p className="font-semibold">
            {t("onboarding.beneficiaries.sub_title")}
          </p>
          <p className="mb-6">{t("onboarding.beneficiaries.description")}</p>
        </div>
      )}

      {loading ? (
        <WizardStepSkeleton />
      ) : error ? (
        <WizardStepError step={1} handlePrevious={handlePrevious} />
      ) : (
        <>
          <BeneficiaryList
            beneficiaries={beneficiaries}
            onboardingProgress={onboardingProgress}
            isUserEnrolled={isUserEnrolled}
            onBeneficiaryChange={handleBeneficiaryChange}
            onBeneficiaryDelete={handleDeleteBeneficiary}
          />

          <BeneficiaryActions
            beneficiariesCount={beneficiaries.length}
            isUserEnrolled={isUserEnrolled}
            isSubmitting={isSubmitting}
            isFormValid={hasNoSpecialChars}
            onAddBeneficiary={handleAddBeneficiary}
            onNext={handleAddOrUpdateBeneficiary}
            onPrevious={handlePrevious}
            onCancel={onCancel}
          />
        </>
      )}
    </div>
  );
};

export default Beneficiaries;
